import React, { useState } from 'react';

const safeMillions = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

/** Presentation-only results view for the regional forecasting workspace. */
const RegionalResultsStage = ({ vm }) => {
  const { Activity, Area, AreaChart, Bar, Calculator, CartesianGrid, ComposedChart, ForecastNiiBuildup, Gauge, Legend, Line, OFFICER_TYPES, ResponsiveContainer, SourceBadge, Tooltip, WaterfallChart, XAxis, YAxis, activeMacro, activeWorkspaceStage, adjustedAnnualSeries, adjustedMonthlySeries, annualResults, annualVarianceWaterfallDomain, annualVarianceWaterfalls, basePortfolio, chartAxisColor, chartGridColor, driverViewAnchor, driverViewDeltas, driverViewDisclosureDeltas, executiveBridgeDomain, executiveBridgeRows, focusedResult, forecastSeries, formatMetricValue, formatMillions: formatMillionsFromVm, formatPercent, ftpAnnual, marketInputs, model, multiYearWaterfallRows, newProductionMonthly, resultsBasis, resultsFocusYear, resultsView, setResultsBasis, setResultsFocusYear, setResultsView, shocks, tooltipStyle, varianceLineRows } = vm;

  const formatMillions = (value, digits) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '—';
    return formatMillionsFromVm(value, digits);
  };

  const formatDeviation = (value) =>
    (value == null || !Number.isFinite(Number(value)))
      ? '—'
      : `${Number(value) >= 0 ? '+' : ''}${Number(value).toFixed(1)}%`;

  const anchorYears = Array.from(
    new Set((driverViewDeltas || []).map((delta) => delta.year))
  ).sort((a, b) => a - b);
  const deltaFor = (source, metric, year) =>
    (source || []).find((delta) => delta.metric === metric && delta.year === year) || null;
  const driverViewBreachCount = (driverViewDeltas || []).filter((delta) => delta.exceeded).length;

  const producerEffect = safeMillions(model.newHireLoanInterestIncome)
    + safeMillions(model.depositFundingBenefit)
    + safeMillions(model.fee12)
    + safeMillions(model.unusedCommitmentFees)
    - safeMillions(model.expense12)
    - safeMillions(model.provision12);

  const [drawersOpen, setDrawersOpen] = useState({
    schedules: false,
    multiYear: false,
    annualSchedule: false
  });

  const collapseAll = () => setDrawersOpen({
    schedules: false,
    multiYear: false,
    annualSchedule: false
  });

  const toggleDrawer = (key) => (event) => {
    const isOpen = event.target.open;
    setDrawersOpen((previous) => (previous[key] === isOpen ? previous : { ...previous, [key]: isOpen }));
  };

  return (
    <>
{activeWorkspaceStage === 'results' && <section className="forecast-panel forecast-results-section workspace-stage-panel" id="regional-stage-results" tabIndex="-1">
        <details className="forecast-accordion" open>
          <summary>
            <div>
              <span className="forecast-section-code">OP_05 · Results</span>
              <h2>Baseline plan vs modified plan variance analysis</h2>
            </div>
            <Calculator size={18} />
          </summary>

          <div className="forecast-accordion-body">
            <div className="forecast-narrative-grid" aria-label="Baseline and modified impact summary">
              <div><span>FY2027 baseline</span><strong>{formatMillions(annualResults[0]?.baseline)}</strong><small>Inherited planning baseline.</small></div>
              <div><span>New-hire 12M effect</span><strong>{formatMillions(producerEffect)}</strong><small>Incremental producer economics.</small></div>
              <div><span>Other lever effect</span><strong>{formatMillions((annualResults[0]?.delta || 0) - producerEffect)}</strong><small>Existing business and assumption effects.</small></div>
              <div><span>FY2027 modified</span><strong>{formatMillions(annualResults[0]?.adjusted)}</strong><small>{annualResults[0] ? `${annualResults[0].variancePct >= 0 ? '+' : ''}${annualResults[0].variancePct.toFixed(1)}% vs baseline` : '—'}</small></div>
            </div>

            {driverViewAnchor && anchorYears.length > 0 && (
              <div className="forecast-panel-lite" data-testid="driver-view-anchor-panel" style={{ marginTop: 8 }}>
                <div className="forecast-panel-head">
                  <div>
                    <span className="forecast-section-code">OP_05 · Driver view</span>
                    <h3>Your plan vs driver view</h3>
                  </div>
                  <SourceBadge kind="central">
                    Base v{driverViewAnchor.scenarioVersion ?? '—'}
                  </SourceBadge>
                </div>
                <small>
                  Top-down driver view (published Base scenario, manual levers zeroed).
                  {driverViewBreachCount > 0
                    ? ` ${driverViewBreachCount} line${driverViewBreachCount === 1 ? '' : 's'} beyond threshold need a justification narrative before submission.`
                    : ' No lines exceed the justification thresholds.'}
                </small>
                <div className="forecast-table-shell ace-scroll-shell forecast-compact-table">
                  <table className="forecast-table">
                    <thead>
                      <tr>
                        <th>Metric</th>
                        {anchorYears.map((year) => (
                          <th key={year}>FY{year}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {['netIncome', 'loans', 'deposits'].map((metric) => (
                        <tr key={metric}>
                          <td>
                            <strong>
                              {metric === 'netIncome' ? 'Net income' : metric === 'loans' ? 'Loans' : 'Deposits'}
                            </strong>
                          </td>
                          {anchorYears.map((year) => {
                            const delta = deltaFor(driverViewDeltas, metric, year);
                            return (
                              <td
                                key={year}
                                className={delta?.exceeded ? 'variance-neg' : ''}
                                title={delta ? `Plan ${formatMillions(delta.plan)} vs driver view ${formatMillions(delta.anchor)}` : undefined}
                              >
                                {delta ? formatDeviation(delta.deviationPct) : '—'}
                                {delta?.exceeded ? ' ●' : ''}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {(driverViewDisclosureDeltas || []).length > 0 && (
                  <details className="workspace-change-drawer">
                    <summary>Supporting lines (NII, fees, expense, provision)</summary>
                    <div className="workspace-change-content forecast-table-shell ace-scroll-shell forecast-compact-table">
                      <table className="forecast-table">
                        <thead>
                          <tr>
                            <th>Metric</th>
                            {anchorYears.map((year) => (
                              <th key={year}>FY{year}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {['nii', 'fees', 'expense', 'provision'].map((metric) => (
                            <tr key={metric}>
                              <td>
                                <strong>
                                  {metric === 'nii' ? 'NII' : metric === 'fees' ? 'Fees' : metric === 'expense' ? 'Expense' : 'Provision'}
                                </strong>
                              </td>
                              {anchorYears.map((year) => {
                                const delta = deltaFor(driverViewDisclosureDeltas, metric, year);
                                return (
                                  <td key={year}>
                                    {delta ? formatDeviation(delta.deviationPct) : '—'}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '4px 0' }}>
              <button type="button" className="forecast-chip-button" onClick={collapseAll}>Collapse all schedules</button>
            </div>

            <details className="workspace-change-drawer" open={drawersOpen.schedules} onToggle={toggleDrawer('schedules')}>
              <summary>Profitability basis and annual financial schedules</summary>
              <div className="workspace-change-content">
            <div className="forecast-basis-tabs" role="group" aria-label="Profitability basis">
              <div className="forecast-segmented">
                {['FTP', 'GAAP'].map((basis) => (
                  <button
                    type="button"
                    key={basis}
                    aria-pressed={resultsBasis === basis}
                    className={resultsBasis === basis ? 'active' : ''}
                    onClick={() => setResultsBasis(basis)}
                  >
                    {basis === 'FTP' ? 'FTP basis (internal)' : 'GAAP basis'}
                  </button>
                ))}
              </div>
              <small>
                {resultsBasis === 'FTP'
                  ? `Funds transfer pricing at SOFR + ${(ftpAnnual.ftpRate - activeMacro.sofr).toFixed(2)}% single-rate curve (${formatPercent(ftpAnnual.ftpRate, 2)}) — internal profitability view`
                  : 'As-reported view — NII without internal transfer charges/credits'}
              </small>
            </div>

            {resultsBasis === 'FTP' ? (
              <div className="forecast-table-shell ace-scroll-shell">
                <table className="forecast-table">
                  <thead>
                    <tr>
                      <th>Fiscal year</th>
                      <th>Lending spread margin</th>
                      <th>Deposit franchise margin</th>
                      <th>Treasury mismatch</th>
                      <th>NII (ties to GAAP)</th>
                      <th>Net income</th>
                      <th>Δ lending vs baseline</th>
                      <th>Δ deposit vs baseline</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ftpAnnual.byYear.map((row) => (
                      <tr key={row.year}>
                        <td><strong>{row.year}</strong></td>
                        <td>{formatMillions(row.plan.lendingMargin)}</td>
                        <td>{formatMillions(row.plan.depositMargin)}</td>
                        <td>{formatMillions(row.plan.mismatch)}</td>
                        <td>{formatMillions(row.plan.nii)}</td>
                        <td>{formatMillions(row.plan.netIncome)}</td>
                        <td className={row.plan.lendingMargin - row.baseline.lendingMargin >= 0 ? 'variance-pos' : 'variance-neg'}>
                          {row.plan.lendingMargin - row.baseline.lendingMargin >= 0 ? '+' : ''}
                          {formatMillions(row.plan.lendingMargin - row.baseline.lendingMargin)}
                        </td>
                        <td className={row.plan.depositMargin - row.baseline.depositMargin >= 0 ? 'variance-pos' : 'variance-neg'}>
                          {row.plan.depositMargin - row.baseline.depositMargin >= 0 ? '+' : ''}
                          {formatMillions(row.plan.depositMargin - row.baseline.depositMargin)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="forecast-table-shell ace-scroll-shell">
                <table className="forecast-table">
                  <thead>
                    <tr>
                      <th>Fiscal year</th>
                      <th>NII</th>
                      <th>Noninterest income</th>
                      <th>Noninterest expense</th>
                      <th>Provision</th>
                      <th>Tax</th>
                      <th>Net income</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adjustedAnnualSeries.map((row) => (
                      <tr key={row.year}>
                        <td><strong>{row.year}</strong></td>
                        <td>{formatMillions(row.nii)}</td>
                        <td>{formatMillions(row.nonInterestIncome)}</td>
                        <td>{formatMillions(row.nonInterestExpense)}</td>
                        <td>{formatMillions(row.creditProvision)}</td>
                        <td>{formatMillions(row.taxExpense)}</td>
                        <td>{formatMillions(row.netIncome)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
              </div>
            </details>

            <div className="forecast-results-toolbar">
              <div>
                <SourceBadge kind="central">Baseline plan</SourceBadge>
                <SourceBadge kind="fpa">Modified plan</SourceBadge>
              </div>
              <div className="forecast-segmented" role="group" aria-label="Results lens">
                {['Variance', 'Plan', 'Modified'].map((view) => (
                  <button
                    type="button"
                    aria-pressed={resultsView === view}
                    className={resultsView === view ? 'active' : ''}
                    onClick={() => setResultsView(view)}
                    key={view}
                  >
                    {view}
                  </button>
                ))}
              </div>
            </div>

            <div className="forecast-executive-bridge">
              <div className="forecast-panel-lite">
                <div className="forecast-panel-head">
                  <div>
                    <span className="forecast-section-code">FY2027 executive bridge</span>
                    <h3>Baseline plan to modified plan</h3>
                  </div>
                </div>
                <WaterfallChart
                  rows={executiveBridgeRows}
                  domain={executiveBridgeDomain}
                  ariaLabel="FY2027 waterfall from baseline plan to modified plan."
                />
              </div>
            </div>

        <details className="workspace-change-drawer" open={drawersOpen.multiYear} onToggle={toggleDrawer('multiYear')}>
          <summary>Multi-year driver waterfalls and focus metrics</summary>
          <div className="workspace-change-content forecast-results-grid">
          <div className="forecast-multi-waterfall-panel">
            <div className="forecast-multi-waterfall-head">
              <div>
                <span className="forecast-section-code">Annual variance waterfalls</span>
                <h3>Baseline net income to modified net income by year</h3>
              </div>
              <SourceBadge kind="fpa">Signed driver bridge</SourceBadge>
            </div>
            <div className="forecast-waterfall-legend" aria-hidden="true">
              <span className="total">Baseline / modified</span>
              <span className="increase">Positive driver</span>
              <span className="decrease">Negative driver</span>
            </div>
            <div className="forecast-multiyear-strip">
              {annualVarianceWaterfalls.map((yearBridge) => (
                <button
                  type="button"
                  className={`forecast-health-chip clickable ${resultsFocusYear === yearBridge.year ? 'gold' : 'neutral'}`}
                  key={yearBridge.year}
                  onClick={() =>
                    setResultsFocusYear((previous) => (previous === yearBridge.year ? null : yearBridge.year))
                  }
                >
                  {yearBridge.year}: {yearBridge.delta >= 0 ? '+' : ''}{formatMillions(yearBridge.delta)} · {yearBridge.largestDriver}
                </button>
              ))}
              {resultsFocusYear !== null && (
                <button type="button" className="forecast-health-chip clickable neutral" onClick={() => setResultsFocusYear(null)}>
                  ✕ Clear focus
                </button>
              )}
            </div>
            <div className="forecast-multiyear-waterfall-wrap">
              <WaterfallChart
                rows={multiYearWaterfallRows}
                domain={annualVarianceWaterfallDomain}
                ariaLabel="Single multi-year waterfall: baseline to modified plan for every forecast year."
                className="multiyear"
              />
            </div>
          </div>

          <div className="forecast-results-summary">
            {focusedResult ? (
              <>
                <div>
                  <span>{focusedResult.year} delta</span>
                  <strong className={focusedResult.delta >= 0 ? 'variance-pos' : 'variance-neg'}>
                    {focusedResult.delta >= 0 ? '+' : ''}{formatMillions(focusedResult.delta)}
                  </strong>
                  <small>{formatPercent(focusedResult.variancePct, 1)} vs baseline · largest driver {focusedResult.largestDriver}.</small>
                </div>
                <div>
                  <span>{focusedResult.year} NII delta</span>
                  <strong className={focusedResult.niiDelta >= 0 ? 'variance-pos' : 'variance-neg'}>
                    {focusedResult.niiDelta >= 0 ? '+' : ''}{formatMillions(focusedResult.niiDelta)}
                  </strong>
                  <small>Fees {focusedResult.feeVariance >= 0 ? '+' : ''}{formatMillions(focusedResult.feeVariance)} · expense {focusedResult.expenseVariance >= 0 ? '+' : ''}{formatMillions(focusedResult.expenseVariance)} · credit {focusedResult.creditVariance >= 0 ? '+' : ''}{formatMillions(focusedResult.creditVariance)}.</small>
                </div>
                <div>
                  <span>{focusedResult.year} loan growth</span>
                  <strong>{formatPercent(focusedResult.loanGrowth)}</strong>
                  <small>{formatPercent(shocks.loanGrowthLimiterPct)} appetite cap applied.</small>
                </div>
                <div>
                  <span>{focusedResult.year} NIM / efficiency</span>
                  <strong>{formatPercent(focusedResult.nim, 2)}</strong>
                  <small>Efficiency {formatPercent(focusedResult.efficiency, 1)} · deposit growth {formatPercent(focusedResult.depositGrowth)}.</small>
                </div>
              </>
            ) : (
              <>
                <div>
                  <span>FY2027 delta</span>
                  <strong>{formatMillions(annualResults[0].delta)}</strong>
                  <small>First fiscal year after market inputs and levers.</small>
                </div>
                <div>
                  <span>FY2031 delta</span>
                  <strong>{formatMillions(annualResults[4].delta)}</strong>
                  <small>Out-years fade toward statistical and macro behavior.</small>
                </div>
                <div>
                  <span>Loan growth</span>
                  <strong>{formatPercent(model.adjustedLoanGrowth)}</strong>
                  <small>{formatPercent(shocks.loanGrowthLimiterPct)} appetite cap applied.</small>
                </div>
                <div>
                  <span>Deposit growth</span>
                  <strong>{formatPercent(model.adjustedDepositGrowth)}</strong>
                  <small>{formatPercent(activeMacro.depositRunoff)} runoff stress in scenario.</small>
                </div>
              </>
            )}
          </div>
          </div>
        </details>

        <details className="workspace-change-drawer" open={drawersOpen.annualSchedule} onToggle={toggleDrawer('annualSchedule')}>
          <summary>Annual plan variance schedule <span>{annualResults.length}</span></summary>
          <div className="workspace-change-content forecast-table-shell ace-scroll-shell forecast-compact-table">
          <table className="forecast-table forecast-annual-table">
            <thead>
              <tr>
                <th>Fiscal year</th>
                <th>Baseline plan NI</th>
                <th>Scenario base NI</th>
                <th>Modified plan NI</th>
                <th>Variance $</th>
                <th>Variance %</th>
                <th>Largest driver</th>
                <th>NII delta</th>
                <th>NIM</th>
                <th>Efficiency</th>
              </tr>
            </thead>
            <tbody>
              {annualResults.map((row) => (
                <tr key={row.year}>
                  <td>
                    <strong>{row.year}</strong>
                  </td>
                  <td>{formatMillions(row.baseline)}</td>
                  <td>{formatMillions(row.scenarioBase)}</td>
                  <td>{formatMillions(row.adjusted)}</td>
                  <td className={row.delta >= 0 ? 'variance-pos' : 'variance-neg'}>
                    {row.delta >= 0 ? '+' : ''}{formatMillions(row.delta)}
                  </td>
                  <td className={row.variancePct >= 0 ? 'variance-pos' : 'variance-neg'}>
                    {row.variancePct >= 0 ? '+' : ''}{formatPercent(row.variancePct, 1)}
                  </td>
                  <td>{row.largestDriver}</td>
                  <td className={row.niiDelta >= 0 ? 'variance-pos' : 'variance-neg'}>
                    {row.niiDelta >= 0 ? '+' : ''}{formatMillions(row.niiDelta)}
                  </td>
                  <td>{formatPercent(row.nim, 2)}</td>
                  <td>{formatPercent(row.efficiency, 1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </details>

        <div className="forecast-table-shell ace-scroll-shell forecast-compact-table">
          <table className="forecast-table forecast-variance-table">
            <thead>
              <tr>
                <th>Line item</th>
                <th>FY2027 baseline</th>
                <th>FY2027 modified</th>
                <th>FY2027 variance</th>
                <th>FY2028 baseline</th>
                <th>FY2028 modified</th>
                <th>FY2028 variance</th>
                <th>5Y base / ending</th>
                <th>5Y modified / ending</th>
                <th>5Y variance</th>
              </tr>
            </thead>
            <tbody>
              {varianceLineRows.map((row) => (
                <tr key={row.key}>
                  <td>
                    <strong>{row.label}</strong>
                  </td>
                  <td>{formatMetricValue(row.baselineFy1, row.type, row.key === 'nim' ? 2 : 1)}</td>
                  <td>{formatMetricValue(row.adjustedFy1, row.type, row.key === 'nim' ? 2 : 1)}</td>
                  <td className={row.varianceFy1 >= 0 ? 'variance-pos' : 'variance-neg'}>{formatMetricValue(row.varianceFy1, row.type, row.key === 'nim' ? 2 : 1)}</td>
                  <td>{formatMetricValue(row.baselineFy2, row.type, row.key === 'nim' ? 2 : 1)}</td>
                  <td>{formatMetricValue(row.adjustedFy2, row.type, row.key === 'nim' ? 2 : 1)}</td>
                  <td className={row.varianceFy2 >= 0 ? 'variance-pos' : 'variance-neg'}>{formatMetricValue(row.varianceFy2, row.type, row.key === 'nim' ? 2 : 1)}</td>
                  <td>{formatMetricValue(row.baselineCumulative, row.type, row.key === 'nim' ? 2 : 1)}</td>
                  <td>{formatMetricValue(row.adjustedCumulative, row.type, row.key === 'nim' ? 2 : 1)}</td>
                  <td className={row.varianceCumulative >= 0 ? 'variance-pos' : 'variance-neg'}>{formatMetricValue(row.varianceCumulative, row.type, row.key === 'nim' ? 2 : 1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
          </div>
        </details>
      </section>}

{activeWorkspaceStage === 'results' && <section className="forecast-panel">
        <details className="forecast-accordion">
          <summary>
            <div>
              <span className="forecast-section-code">OP_04B · Portfolio and blend</span>
              <h2>Balance path, NII path, and manual fade logic</h2>
            </div>
            <Activity size={18} />
          </summary>

          <div className="forecast-accordion-body">
            <ForecastNiiBuildup
              adjustedMonthly={adjustedMonthlySeries}
              newProductionMonthly={newProductionMonthly}
              officerTypes={OFFICER_TYPES}
              marketInputs={marketInputs}
              activeMacro={activeMacro}
              basePortfolio={basePortfolio}
            />
            <div className="forecast-grid forecast-grid-2 forecast-grid-contained">
        <div className="forecast-panel">
          <div className="forecast-panel-head">
            <div>
              <span className="forecast-section-code">OP_04B · Portfolio engine</span>
              <h2>Balance and NII path</h2>
            </div>
            <Activity size={18} />
          </div>
          <div className="forecast-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={forecastSeries} margin={{ top: 10, right: 10, left: 0, bottom: 4 }}>
                <CartesianGrid stroke={chartGridColor} vertical={false} strokeDasharray="3 5" />
                <XAxis dataKey="month" tick={{ fill: chartAxisColor, fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fill: chartAxisColor, fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fill: chartAxisColor, fontSize: 11 }} />
                <Tooltip contentStyle={tooltipStyle} formatter={(value, name) => {
                  if (name === 'nii') return formatMillions(value, 1);
                  return formatMillions(value, 0);
                }} />
                <Legend wrapperStyle={{ color: chartAxisColor, fontSize: 11 }} />
                <Bar yAxisId="left" dataKey="loans" name="Loans" fill="hsl(var(--chart-3))" radius={[5, 5, 0, 0]} />
                <Bar yAxisId="left" dataKey="deposits" name="Deposits" fill="hsl(var(--chart-2))" radius={[5, 5, 0, 0]} />
                <Line yAxisId="right" dataKey="nii" name="NII" stroke="hsl(var(--chart-1))" strokeWidth={2.5} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="forecast-panel">
          <div className="forecast-panel-head">
            <div>
              <span className="forecast-section-code">OP_04C · Blend logic</span>
              <h2>Manual fades to baseline</h2>
            </div>
            <Gauge size={18} />
          </div>
          <div className="forecast-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={forecastSeries} margin={{ top: 10, right: 10, left: 0, bottom: 4 }}>
                <CartesianGrid stroke={chartGridColor} vertical={false} strokeDasharray="3 5" />
                <XAxis dataKey="month" tick={{ fill: chartAxisColor, fontSize: 11 }} />
                <YAxis tick={{ fill: chartAxisColor, fontSize: 11 }} />
                <Tooltip contentStyle={tooltipStyle} formatter={(value) => formatPercent(value, 0)} />
                <Area
                  type="monotone"
                  dataKey="manualWeight"
                  name="Market input"
                  stackId="1"
                  stroke="hsl(var(--chart-1))"
                  fill="hsl(var(--chart-1))"
                />
                <Area
                  type="monotone"
                  dataKey="baselineWeight"
                  name="Central/statistical"
                  stackId="1"
                  stroke="hsl(var(--chart-2))"
                  fill="hsl(var(--chart-2))"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

            </div>
          </div>
        </details>
      </section>}
    </>
  );
};

export default RegionalResultsStage;
